import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AuthCard from '../components/AuthCard';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import ReCAPTCHA from 'react-google-recaptcha';
import api from '../config/api';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || 'invalid-site-key';

const Login = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [lastErrorKey, setLastErrorKey] = useState(null);
  const [lastErrorMsg, setLastErrorMsg] = useState(null);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [recaptchaLoadError, setRecaptchaLoadError] = useState(false);
  const { login, setAuthData } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    try {
      if (sessionStorage.getItem('baristaSubscriptionExpired') === '1') {
        setLastErrorKey('barista.subscriptionInactive');
        setLastErrorMsg(t('barista.subscriptionAutoLogout'));
        sessionStorage.removeItem('baristaSubscriptionExpired');
      }
    } catch (_) { /* ignore */ }
    if (process.env.NODE_ENV !== 'production') {
      // Dev helper: confirm env injection
      // These are safe to log (public values used by the browser)
      // eslint-disable-next-line no-console
      console.debug('[Login] GOOGLE_CLIENT_ID present:', Boolean(GOOGLE_CLIENT_ID));
      // eslint-disable-next-line no-console
      console.debug('[Login] RECAPTCHA_SITE_KEY present:', Boolean(RECAPTCHA_SITE_KEY));
    }
  }, [t]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Require captcha token only when the widget loaded without error.
    if (!recaptchaLoadError && !recaptchaToken) {
      toast.error(t('auth.recaptchaRequired', 'Please complete the reCAPTCHA'));
      return;
    }
    
    setLoading(true);
    setLastErrorKey(null);
    setLastErrorMsg(null);

    const result = await login(formData.email, formData.password, recaptchaToken);

    if (result.success) {
      toast.success(t('common.success'));
      const user = result.user;
      if (user.role === 'ADMIN' || user.email === 'admin@coffeecloud.com') navigate('/admin');
      else if (user.role === 'OWNER') navigate('/dashboard');
      else if (user.role === 'BARISTA') navigate('/barista');
      else navigate('/dashboard');
    } else {
      if (result.errorKey === 'barista.subscriptionInactive') {
        setLastErrorKey(result.errorKey);
        setLastErrorMsg(result.error);
        toast.error(t('barista.subscriptionInactive', 'Your coffee shop subscription is inactive. Ask the owner to renew.'));
      } else {
        toast.error(result.error);
        setLastErrorMsg(result.error);
      }
    }
    
    setLoading(false);
  };
  
  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      setLoading(true);
      const response = await api.post('/auth/google', {
        idToken: credentialResponse.credential
      });
      
      if (response.data.success) {
        setAuthData(response.data.token, response.data.user);
        toast.success(t('auth.google.success', 'Signed in with Google successfully!'));
        
        const user = response.data.user;
        if (user.role === 'ADMIN') navigate('/admin');
        else if (user.role === 'OWNER') navigate('/dashboard');
        else if (user.role === 'BARISTA') navigate('/barista');
        else navigate('/dashboard');
      } else {
        toast.error(response.data.message || t('auth.google.failed', 'Google sign-in failed'));
      }
    } catch (error) {
      toast.error(error.response?.data?.message || t('auth.google.failed', 'Google sign-in failed'));
    } finally {
      setLoading(false);
    }
  };
  
  const handleGoogleError = () => {
    toast.error(t('auth.google.failed', 'Google sign-in failed'));
  };

  return (
    <AuthCard subtitle={t('auth.login.subtitle')}>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
            {t('auth.login.email')}
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            className="input-field"
            placeholder={t('auth.login.email')}
          />
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2">
            {t('auth.login.password')}
          </label>
          <input
            type="password"
            id="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            required
            className="input-field"
            placeholder={t('auth.login.password')}
          />
        </div>

        {lastErrorKey === 'barista.subscriptionInactive' && (
          <div className="rounded border border-red-500/40 bg-red-900/30 p-3 text-sm text-red-300">
            {t('barista.subscriptionInactive', 'Your coffee shop subscription is inactive. Ask the owner to renew.')}<br />
            <span className="text-xs text-red-400/70">{lastErrorMsg}</span>
          </div>
        )}
        {!lastErrorKey && lastErrorMsg && (
          <div className="rounded border border-red-500/40 bg-red-900/30 p-3 text-sm text-red-300">
            {lastErrorMsg}
          </div>
        )}
        
        <div className="flex justify-center">
          <ReCAPTCHA
            sitekey={RECAPTCHA_SITE_KEY}
            onChange={(token) => setRecaptchaToken(token)}
            onExpired={() => setRecaptchaToken(null)}
            onErrored={() => setRecaptchaLoadError(true)}
            theme="dark"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          className="btn-primary w-full"
        >
          {loading ? t('common.loading') : t('auth.login.signIn')}
        </button>
      </form>
      
      {GOOGLE_CLIENT_ID && (
        <>
          <div className="mt-6 relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-700"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-gray-900 text-gray-400">{t('auth.orContinueWith', 'Or continue with')}</span>
            </div>
          </div>
          
          <div className="mt-6">
            <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                theme="filled_black"
                size="large"
                width="100%"
                text="signin_with"
              />
            </GoogleOAuthProvider>
          </div>
        </>
      )}

      <div className="mt-6 text-center">
        <p className="text-gray-300">
          {t('auth.login.noAccount')}{' '}
          <Link to="/register" className="text-[#a15507] hover:text-[#924e06] font-medium transition-colors duration-200">
            {t('auth.login.signUp')}
          </Link>
        </p>
      </div>
    </AuthCard>
  );
};

export default Login;